import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type { ClientLinkCreateResultV1, StudioDepartureKitStatusV1, StudioIntakeSummaryV1 } from "@enrein/shared-contracts";
import type { BusinessGraphActiveViewV1 } from "@enrein/shared-contracts/studio-project-workflow";
import type { StudioRunRowV1 } from "@enrein/shared-contracts/studio-dashboard";
import { createStudioApiClient } from "../../api.js";
import { clientLinkUrl } from "../../client-link-url.js";
import { readClientLinkSession, saveClientLinkSession } from "../../client-link-session-store.js";
import { readStudioRuntimeConfig } from "../../env.js";
import { resolveSharePreviewUrl } from "../../resolve-share-preview-url.js";
import { usePolling } from "../../usePolling.js";
import {
  agencyEnterProject,
  confirmFactsAndBuildPreview,
  createIntakeLinkProject,
  formatWorkflowError,
  type PackageEligibilityBlocked,
} from "../studio-shared/workflow/studio-workflow.js";
import { sitePackageDisplayLabel } from "../studio-shared/site-package-labels.js";
import type { SitePackageId } from "@enrein/shared-contracts";
import { guidedToStudioFormInput } from "../studio-shared/types/guided-new-project.js";
import type { GuidedStartMethod } from "../studio-shared/types/guided-new-project.js";
import {
  deriveCommandCenterStats,
  deriveGuidedNextActions,
  deriveSimpleProjectFacts,
  humanIntakeStatus,
  humanRunStatusLabel,
  isHandoffReadyDepartureKit,
  mapLifecycleViewToPhaseState,
} from "./adapters/guided-project-steps.js";
import type { CommandCenterStatCard } from "./adapters/guided-project-steps.js";
import { deriveProofChainSteps } from "../../derive-studio-actions.js";
import { parseGuidedRoute, pushGuidedRoute } from "./routing/guided-routing.js";
import type { GuidedView } from "./routing/guided-views.js";
import {
  departureKitFromLifecycleSummary,
  reviewFromLifecycleSummary,
} from "../studio-shared/orchestration/useStudioProjects.js";
import { refreshProjectSnapshot as loadProjectSnapshot } from "../studio-shared/orchestration/project-snapshot.js";
import type { StudioProjectLifecycleViewV1 } from "@enrein/shared-contracts";
import { GuidedShell } from "./GuidedShell.js";
import {
  PREVIEW_UNAVAILABLE_MESSAGE,
  getPreviewOpenBlockReason,
  isPreviewOpenAllowed,
  logPreviewOpenGuardDebug,
  type PreviewOpenGuardInput,
} from "../studio-shared/preview-open-guard.js";

export function EnreinGuidedApp({ agencyId }: { readonly agencyId: string }) {
  const config = useMemo(() => readStudioRuntimeConfig(), []);
  const api = useMemo(() => createStudioApiClient(config, { agencyId }), [agencyId, config]);
  const [route, setRoute] = useState(() => parseGuidedRoute(window.location.search));
  const [toast, setToast] = useState<string | null>(null);
  const [clientReview, setClientReview] = useState<ClientReviewStatusV1 | null>(null);
  const [bgStatus, setBgStatus] = useState<Awaited<ReturnType<typeof api.getBusinessGraphStatus>> | null>(null);
  const [activeGraph, setActiveGraph] = useState<BusinessGraphActiveViewV1 | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [, setRuns] = useState<readonly StudioRunRowV1[]>([]);
  const [lastClientLink, setLastClientLink] = useState<ClientLinkCreateResultV1 | null>(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [validationIssues, setValidationIssues] = useState<readonly string[]>([]);
  const [fastPollUntil, setFastPollUntil] = useState<number | null>(null);
  const [intakeSummary, setIntakeSummary] = useState<StudioIntakeSummaryV1 | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectLoadError, setProjectLoadError] = useState<string | null>(null);
  const [projectReviews, setProjectReviews] = useState<Record<string, ClientReviewStatusV1 | null>>({});
  const [projectBusinessNames, setProjectBusinessNames] = useState<Record<string, string | null>>({});
  const [departureKit, setDepartureKit] = useState<StudioDepartureKitStatusV1 | null>(null);
  const [lifecycle, setLifecycle] = useState<StudioProjectLifecycleViewV1 | null>(null);
  const [projectDepartureKits, setProjectDepartureKits] = useState<Record<string, StudioDepartureKitStatusV1 | null>>({});
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [confirmationCount, setConfirmationCount] = useState(0);
  const [selectedOpportunityClaimId, setSelectedOpportunityClaimId] = useState<string | null>(null);
  const [packageEligibilityBlocked, setPackageEligibilityBlocked] =
    useState<PackageEligibilityBlocked | null>(null);
  const generationOptionsRef = useRef({ sitePackageId: "package-standard-5", lensId: "lens-flat-2" });

  const selectedProjectId = route.projectId;
  const pollMs =
    agencyId.length > 0 ? (fastPollUntil && Date.now() < fastPollUntil ? 2000 : config.pollIntervalMs) : 0;

  const loadProjectSummaries = useCallback(() => api.listProjectSummaries(), [api]);
  const projectsState = usePolling(loadProjectSummaries, pollMs);

  const loadMissionControl = useCallback(() => api.getMissionControlSummary(), [api]);
  const missionControlState = usePolling(loadMissionControl, pollMs);

  const loadScanOpportunityCount = useCallback(async () => {
    try {
      const list = await api.listPublicScanOpportunities();
      return list.opportunities.length;
    } catch {
      return 0;
    }
  }, [api]);
  const scanOpportunitiesState = usePolling(loadScanOpportunityCount, pollMs);

  useEffect(() => {
    const onPop = () => setRoute(parseGuidedRoute(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const reloadSelectedProject = useCallback(async () => {
    if (!selectedProjectId) return;
    const snapshot = await loadProjectSnapshot(api, selectedProjectId);
    setClientReview(snapshot.review);
    setBgStatus(snapshot.status);
    setRuns(snapshot.runRows);
    setBusinessName(snapshot.businessName);
    setIntakeSummary(snapshot.intakeSummary);
    setActiveGraph(snapshot.activeGraph);
    setDepartureKit(snapshot.departureKit);
    setLifecycle(snapshot.lifecycle);
    setProjectReviews((current) => ({ ...current, [selectedProjectId]: snapshot.review }));
    if (snapshot.departureKit) {
      setProjectDepartureKits((current) => ({ ...current, [selectedProjectId]: snapshot.departureKit }));
    }
  }, [api, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setClientReview(null);
      setBgStatus(null);
      setActiveGraph(null);
      setBusinessName(null);
      setRuns([]);
      setIntakeSummary(null);
      setDepartureKit(null);
      setLifecycle(null);
      setHandoffError(null);
      setProjectLoading(false);
      setProjectLoadError(null);
      return;
    }
    let cancelled = false;
    setProjectLoading(true);
    setProjectLoadError(null);
    void (async () => {
      try {
        const snapshot = await loadProjectSnapshot(api, selectedProjectId);
        if (cancelled) return;
        setClientReview(snapshot.review);
        setBgStatus(snapshot.status);
        setRuns(snapshot.runRows);
        setBusinessName(snapshot.businessName);
        setIntakeSummary(snapshot.intakeSummary);
        setActiveGraph(snapshot.activeGraph);
        setDepartureKit(snapshot.departureKit);
        setLifecycle(snapshot.lifecycle);
        setProjectReviews((current) => ({ ...current, [selectedProjectId]: snapshot.review }));
        if (snapshot.departureKit) {
          setProjectDepartureKits((current) => ({ ...current, [selectedProjectId]: snapshot.departureKit }));
        }
      } catch (error) {
        if (!cancelled) {
          setClientReview(null);
          setBgStatus(null);
          setActiveGraph(null);
          setRuns([]);
          setIntakeSummary(null);
          setProjectLoadError(formatWorkflowError(error));
        }
      } finally {
        if (!cancelled) {
          setProjectLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, selectedProjectId]);

  useEffect(() => {
    const allProjects = projectsState.data ?? [];
    if (allProjects.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const lifecycleSummaries = await api.listProjectLifecycleSummaries();
        if (cancelled) return;
        setProjectBusinessNames(
          Object.fromEntries(
            lifecycleSummaries.map((row) => [row.projectId, row.businessName ?? null]),
          ),
        );
        setProjectReviews(
          Object.fromEntries(lifecycleSummaries.map((row) => [row.projectId, reviewFromLifecycleSummary(row)])),
        );
        setProjectDepartureKits(
          Object.fromEntries(
            lifecycleSummaries.map((row) => [row.projectId, departureKitFromLifecycleSummary(row)]),
          ),
        );
      } catch {
        if (!cancelled) {
          setProjectBusinessNames({});
          setProjectReviews({});
          setProjectDepartureKits({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, projectsState.data]);

  const previewResolution = useMemo(
    () =>
      resolveSharePreviewUrl({
        clientReview,
        lastClientLink,
        storedSession: selectedProjectId ? readClientLinkSession(selectedProjectId) : null,
        config,
      }),
    [clientReview, config, lastClientLink, selectedProjectId],
  );

  const previewUrl = previewResolution.kind === "ready" ? previewResolution.url : null;

  const portalUrl = useMemo(() => {
    if (previewUrl) return previewUrl;
    const session = selectedProjectId ? readClientLinkSession(selectedProjectId) : null;
    if (session) return clientLinkUrl(config, session.shellPath);
    if (lastClientLink) return clientLinkUrl(config, lastClientLink.shellPath);
    const base = config.clientPortalBaseUrl.trim();
    return base.length > 0 ? base : null;
  }, [config, lastClientLink, previewUrl, selectedProjectId]);

  const phaseState = useMemo(() => {
    if (!selectedProjectId || !lifecycle) return null;
    const facts = deriveSimpleProjectFacts({
      intakeSummary,
      activeGraph,
      businessName,
    });
    return mapLifecycleViewToPhaseState(lifecycle, facts);
  }, [activeGraph, businessName, intakeSummary, lifecycle, selectedProjectId]);

  const selectedProjectSummary = useMemo(
    () => (projectsState.data ?? []).find((project) => project.projectId === selectedProjectId) ?? null,
    [projectsState.data, selectedProjectId],
  );

  const previewOpenGuardInput = useMemo((): PreviewOpenGuardInput => ({
    previewHtml: null,
    previewUrl,
    previewVersionId: clientReview?.latestPreview?.previewVersionId ?? null,
    qaFailure: phaseState?.qaFailure ?? false,
    latestRunStatus: selectedProjectSummary?.lastKnownRunStatus ?? null,
  }), [
    clientReview?.latestPreview?.previewVersionId,
    phaseState?.qaFailure,
    previewUrl,
    selectedProjectSummary?.lastKnownRunStatus,
  ]);

  const previewOpenAllowed = isPreviewOpenAllowed(previewOpenGuardInput);

  const hasClientApproval = Boolean(clientReview?.latestApproval);

  const nextActions = useMemo(
    () =>
      deriveGuidedNextActions({
        projects: (projectsState.data ?? []).map((project) => ({
          projectId: project.projectId,
          businessName: projectBusinessNames[project.projectId] ?? null,
          lastKnownRunStatus: project.lastKnownRunStatus,
        })),
        reviews: projectReviews,
        departureKits: projectDepartureKits,
      }),
    [projectBusinessNames, projectDepartureKits, projectReviews, projectsState.data],
  );

  const commandCenterStats = useMemo(
    (): readonly CommandCenterStatCard[] =>
      deriveCommandCenterStats({
        missionSummary: missionControlState.data,
        scanOpportunityCount: scanOpportunitiesState.data ?? 0,
      }),
    [missionControlState.data, scanOpportunitiesState.data],
  );

  const commandCenterStatsLoading =
    missionControlState.loading || scanOpportunitiesState.loading || projectsState.loading;
  const commandCenterStatsError =
    missionControlState.error && scanOpportunitiesState.error
      ? `${missionControlState.error}; ${scanOpportunitiesState.error}`
      : missionControlState.error ?? scanOpportunitiesState.error;

  const projectRows = useMemo(
    () =>
      (projectsState.data ?? []).map((project) => ({
        projectId: project.projectId,
        name: (projectBusinessNames[project.projectId] ?? project.projectId.replace(/^project_/, "")) || "Client",
        status: project.currentBusinessGraphRevisionId
          ? "Business Profile active"
          : humanRunStatusLabel(project.lastKnownRunStatus),
      })),
    [projectBusinessNames, projectsState.data],
  );

  const proofChainSteps = useMemo(() => {
    if (!selectedProjectId) return [];
    return deriveProofChainSteps({
      hasReport: Boolean(bgStatus?.currentBusinessGraphRevisionId),
      clientReview,
      hasLaunchProof: Boolean(departureKit && departureKit.issuanceStatus !== "BLOCKED"),
      handoffReady: Boolean(departureKit?.downloadAvailable),
    });
  }, [bgStatus?.currentBusinessGraphRevisionId, clientReview, departureKit, selectedProjectId]);

  const refreshConfirmationCount = useCallback(async () => {
    try {
      const queue = await api.listImportConfirmationQueue();
      setConfirmationCount(queue.entries.filter((entry) => entry.pendingDecisionCount > 0).length);
    } catch {
      setConfirmationCount(0);
    }
  }, [api]);

  const navigate = useCallback((view: GuidedView, projectId?: string, startMethod?: GuidedStartMethod | null) => {
    pushGuidedRoute({ view, projectId: projectId ?? null, startMethod: startMethod ?? null });
    setRoute({ view, projectId: projectId ?? null, startMethod: startMethod ?? null });
  }, []);

  const handleScanComplete = useCallback(
    async (publicReportRef: string) => {
      try {
        await api.claimPublicReport(publicReportRef);
        setToast("Scan complete â€” opportunity claimed.");
        scanOpportunitiesState.refresh();
        navigate("scan-opportunities");
      } catch (error) {
        setToast(formatWorkflowError(error));
      }
    },
    [api, navigate, scanOpportunitiesState],
  );

  useEffect(() => {
    if (route.view !== "more") return;
    let cancelled = false;
    void (async () => {
      try {
        const queue = await api.listImportConfirmationQueue();
        const pending = queue.entries.filter((entry) => entry.pendingDecisionCount > 0).length;
        if (!cancelled) setConfirmationCount(pending);
      } catch {
        if (!cancelled) setConfirmationCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, route.view]);

  useEffect(() => {
    if (!selectedProjectId) return;
    const generating = lifecycle?.flags.generating ?? false;
    const packaging = lifecycle?.flags.packaging ?? false;
    const fastPoll = fastPollUntil !== null && Date.now() < fastPollUntil;
    const handoffFastPoll =
      fastPoll && hasClientApproval && !isHandoffReadyDepartureKit(departureKit);
    if (!generating && !packaging && !handoffFastPoll) return;

    const intervalMs =
      fastPoll || generating || packaging
        ? (lifecycle?.pollHints.suggestedIntervalMs ?? 2000)
        : config.pollIntervalMs;
    const id = window.setInterval(() => {
      void reloadSelectedProject();
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [
    config.pollIntervalMs,
    departureKit,
    fastPollUntil,
    hasClientApproval,
    lifecycle?.flags.generating,
    lifecycle?.flags.packaging,
    lifecycle?.pollHints.suggestedIntervalMs,
    reloadSelectedProject,
    selectedProjectId,
  ]);

  useEffect(() => {
    if (!isHandoffReadyDepartureKit(departureKit)) return;
    if (fastPollUntil === null) return;
    setFastPollUntil(null);
    setToast("Handoff ready — download your Departure Kit.");
  }, [departureKit, fastPollUntil]);

  const handlePackageHandoff = useCallback(async () => {
    if (!selectedProjectId || !phaseState?.exportRunId) {
      setToast("Export run is not ready yet.");
      return;
    }
    setWorkflowBusy(true);
    setHandoffError(null);
    try {
      await api.requestRunExport(phaseState.exportRunId, selectedProjectId);
      setToast("Packaging handoffâ€¦");
      setFastPollUntil(Date.now() + 180_000);
      await reloadSelectedProject();
    } catch (error) {
      const message = formatWorkflowError(error);
      setHandoffError(message);
      setToast(message);
    } finally {
      setWorkflowBusy(false);
    }
  }, [api, phaseState?.exportRunId, reloadSelectedProject, selectedProjectId]);

  const handleDownloadHandoff = useCallback(async () => {
    if (!selectedProjectId) return;
    setWorkflowBusy(true);
    setHandoffError(null);
    try {
      const blob = await api.downloadDepartureKit(selectedProjectId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `departure-kit-${selectedProjectId}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      setToast("Departure Kit download started.");
    } catch (error) {
      const message = formatWorkflowError(error);
      setHandoffError(message);
      setToast(message);
    } finally {
      setWorkflowBusy(false);
    }
  }, [api, selectedProjectId]);

  const copyClientLink = useCallback(
    async (projectIdOverride?: string) => {
      const pid = projectIdOverride ?? selectedProjectId;
      if (!pid) {
        setToast("Select a project first.");
        return;
      }
      try {
        const result = await api.createClientLink(pid, { clientDisplayName: businessName ?? undefined });
        setLastClientLink(result);
        saveClientLinkSession(pid, result);
        const url = clientLinkUrl(config, result.shellPath);
        await navigator.clipboard.writeText(url);
        setToast("Client link copied.");
        if (pid === selectedProjectId) await reloadSelectedProject();
      } catch (error) {
        setToast(formatWorkflowError(error));
      }
    },
    [api, businessName, config, reloadSelectedProject, selectedProjectId],
  );

  const copyPreviewLink = useCallback(async () => {
    const blockReason = getPreviewOpenBlockReason(previewOpenGuardInput);
    if (blockReason) {
      setToast(blockReason);
      return;
    }
    if (!previewUrl) {
      setToast(PREVIEW_UNAVAILABLE_MESSAGE);
      return;
    }
    await navigator.clipboard.writeText(previewUrl);
    setToast("Preview link copied.");
  }, [previewOpenGuardInput, previewUrl]);

  const handleConfirmBuild = useCallback(async () => {
    if (!selectedProjectId) return;
    setWorkflowBusy(true);
    setValidationIssues([]);
    try {
      const result = await confirmFactsAndBuildPreview(api, selectedProjectId, {
        intakeSummary,
        latestDraftId: bgStatus?.latestDraftId ?? null,
        currentBusinessGraphRevisionId: bgStatus?.currentBusinessGraphRevisionId ?? null,
        sitePackageId: generationOptionsRef.current.sitePackageId,
        lensId: generationOptionsRef.current.lensId,
      });
      if (result.kind === "validation_blocked") {
        setValidationIssues(result.issues.length > 0 ? result.issues : ["Validation failed — fix facts before building."]);
        setToast("Fix validation issues before building preview.");
        return;
      }
      if (result.kind === "package_eligibility_blocked") {
        setPackageEligibilityBlocked(result.blocked);
        setValidationIssues([
          `Profile is not ready for ${sitePackageDisplayLabel(result.blocked.requestedPackage)}.`,
        ]);
        setToast(
          `Profile is not ready for ${sitePackageDisplayLabel(result.blocked.requestedPackage)} — choose an option below.`,
        );
        return;
      }
      setPackageEligibilityBlocked(null);
      setToast("Generating website…");
      setFastPollUntil(Date.now() + 120_000);
      await reloadSelectedProject();
      await projectsState.refresh();
    } catch (error) {
      setToast(formatWorkflowError(error));
    } finally {
      setWorkflowBusy(false);
    }
  }, [api, bgStatus, intakeSummary, projectsState, reloadSelectedProject, selectedProjectId]);

  const handleBuildWithPackage = useCallback(
    async (sitePackageId: SitePackageId) => {
      if (!selectedProjectId) return;
      generationOptionsRef.current.sitePackageId = sitePackageId;
      setWorkflowBusy(true);
      setValidationIssues([]);
      try {
        const result = await confirmFactsAndBuildPreview(api, selectedProjectId, {
          intakeSummary,
          latestDraftId: bgStatus?.latestDraftId ?? null,
          currentBusinessGraphRevisionId: bgStatus?.currentBusinessGraphRevisionId ?? null,
          sitePackageId,
          lensId: generationOptionsRef.current.lensId,
        });
        if (result.kind === "validation_blocked") {
          setValidationIssues(result.issues.length > 0 ? result.issues : ["Validation failed — fix facts before building."]);
          setToast("Fix validation issues before building preview.");
          return;
        }
        if (result.kind === "package_eligibility_blocked") {
          setPackageEligibilityBlocked(result.blocked);
          setToast(
            `Profile is not ready for ${sitePackageDisplayLabel(result.blocked.requestedPackage)} — choose an option below.`,
          );
          return;
        }
        setPackageEligibilityBlocked(null);
        setToast(`Generating ${sitePackageDisplayLabel(sitePackageId)} site…`);
        setFastPollUntil(Date.now() + 120_000);
        await reloadSelectedProject();
        await projectsState.refresh();
      } catch (error) {
        setToast(formatWorkflowError(error));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [api, bgStatus, intakeSummary, projectsState, reloadSelectedProject, selectedProjectId],
  );

  const handleEnrichProfile = useCallback(() => {
    void copyClientLink();
    setToast("Share the client link so your client can add more business details.");
  }, [copyClientLink]);

  const openClientPortal = useCallback(() => {
    if (!portalUrl) {
      setToast("Client portal link is not ready â€” copy a client link first.");
      return;
    }
    window.open(portalUrl, "_blank", "noopener,noreferrer");
  }, [portalUrl]);

  const openPreview = useCallback(() => {
    logPreviewOpenGuardDebug("guided-open", previewOpenGuardInput);
    const blockReason = getPreviewOpenBlockReason(previewOpenGuardInput);
    if (blockReason) {
      setToast(blockReason);
      return;
    }
    if (!previewUrl) {
      setToast(PREVIEW_UNAVAILABLE_MESSAGE);
      return;
    }
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  }, [previewOpenGuardInput, previewUrl]);

  const handlePrimaryAction = useCallback(
    (actionId: string) => {
      if (actionId === "confirm-build") void handleConfirmBuild();
      else if (actionId === "client-link") void copyClientLink();
      else if (actionId === "copy-preview") void copyPreviewLink();
      else if (actionId === "open-portal") openClientPortal();
      else if (actionId === "package-handoff") void handlePackageHandoff();
      else if (actionId === "download-handoff") void handleDownloadHandoff();
    },
    [copyClientLink, copyPreviewLink, handleConfirmBuild, handleDownloadHandoff, handlePackageHandoff, openClientPortal],
  );

  const handleNewProject = useCallback(
    async (input: {
      businessName: string;
      contact: string;
      projectType: string;
      startMethod: GuidedStartMethod;
      demoPreset?: import("../studio-shared/types/guided-demo-presets.js").GuidedStudioDemoPreset | null;
    }) => {
      setWorkflowBusy(true);
      setValidationIssues([]);
      const studioInput = guidedToStudioFormInput(input);
      try {
        if (input.startMethod === "intake") {
          const result = await createIntakeLinkProject(api, config, studioInput);
          await navigator.clipboard.writeText(result.intakeUrl);
          setToast("Client link copied â€” share with your client.");
          await projectsState.refresh();
          navigate("project", result.projectId);
        } else {
          const result = await agencyEnterProject(api, studioInput);
          setToast("Project created.");
          await projectsState.refresh();
          navigate("project", result.projectId);
        }
      } catch (error) {
        const message = formatWorkflowError(error);
        setValidationIssues([message]);
        setToast(message);
      } finally {
        setWorkflowBusy(false);
      }
    },
    [api, config, navigate, projectsState],
  );

  const handleHomeAction = useCallback(
    (_actionId: string, projectId: string) => {
      navigate("project", projectId);
    },
    [navigate],
  );


  return (
    <GuidedShell
      view={route.view}
      projectId={route.projectId}
      startMethod={route.startMethod}
      toast={toast}
      workflowBusy={workflowBusy}
      validationIssues={validationIssues}
      businessName={businessName ?? intakeSummary?.businessName ?? "Client"}
      phaseState={phaseState}
      intakeSummary={intakeSummary}
      intakeStatusLabel={humanIntakeStatus(intakeSummary)}
      projectRows={projectRows}
      projectsLoading={projectsState.loading}
      projectsReady={projectsState.ready}
      projectsError={projectsState.error ? formatWorkflowError(projectsState.error) : null}
      projectLoading={projectLoading}
      projectLoadError={projectLoadError}
      previewUrl={previewUrl}
      previewOpenAllowed={previewOpenAllowed}
      departureKit={departureKit}
      handoffError={handoffError}
      nextActions={nextActions}
      commandCenterStats={commandCenterStats}
      commandCenterStatsLoading={commandCenterStatsLoading}
      commandCenterStatsError={commandCenterStatsError}
      confirmationCount={confirmationCount}
      proofChainSteps={proofChainSteps}
      api={api}
      selectedOpportunityClaimId={selectedOpportunityClaimId}
      onSelectedOpportunityClaimIdChange={setSelectedOpportunityClaimId}
      onScanComplete={(ref) => void handleScanComplete(ref)}
      onConfirmationQueueChanged={() => void refreshConfirmationCount()}
      onNavigate={navigate}
      onNewProject={(input) => void handleNewProject(input)}
      onPrimaryAction={handlePrimaryAction}
      onOpenPortal={openClientPortal}
      onOpenPreview={openPreview}
      onHomeAction={handleHomeAction}
      packageEligibilityBlocked={packageEligibilityBlocked}
      onBuildWithPackage={(pkg) => void handleBuildWithPackage(pkg)}
      onEnrichProfile={handleEnrichProfile}
    />
  );
}
